import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Grid, Card, CardMedia, Box, Typography, Button, TextField, Avatar, Menu, MenuItem,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
    InputLabel, Select, Chip, Autocomplete, Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
// Icons will be imported as needed
// DatePicker będzie dodany później - na razie użyjemy TextField z type="date"
import styles from './Pairing.module.css';
import Logo from '../../assets/logo_white.png';
import AvatarImage from '../../assets/PigeonsCatalog/Avatar.png';
import customIcon1 from '../../assets/PigeonsCatalog/pearson_catalog.svg';
import customIcon2 from '../../assets/PigeonsCatalog/wing_catalog.svg';
import customIcon3 from '../../assets/PigeonsCatalog/pair_catalog.svg';
import customIcon4 from '../../assets/PigeonsCatalog/genealogy_catalog.svg';
import customIcon5 from '../../assets/PigeonsCatalog/flight_catalog.svg';
import pigeonImage from '../../assets/main-pigeon.png';

const Pairing = () => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [pairs, setPairs] = useState([]);
    const [pigeons, setPigeons] = useState([]);
    const [addPairOpen, setAddPairOpen] = useState(false);
    const [editPairOpen, setEditPairOpen] = useState(false);
    const [selectedPair, setSelectedPair] = useState(null);
    const [pairDetailsOpen, setPairDetailsOpen] = useState(false);
    const [filter, setFilter] = useState('all'); // 'all', 'active', 'completed', 'season2026', 'best'
    const [pairForm, setPairForm] = useState({
        maleId: '',
        femaleId: '',
        pairingDate: new Date().toISOString().split('T')[0], // Format YYYY-MM-DD
        status: 'planned', // 'planned', 'in_nest', 'eggs', 'hatched', 'weaned', 'completed'
        comment: '',
        purpose: '' // 'racing', 'sale', 'line_test'
    });

    // Wczytywanie gołębi z localStorage
    useEffect(() => {
        const savedPigeons = localStorage.getItem('pigeonsData');
        if (savedPigeons) {
            try {
                const parsed = JSON.parse(savedPigeons);
                setPigeons(parsed);
            } catch (error) {
                console.error('Błąd wczytywania gołębi:', error);
            }
        }
    }, []);

    // Wczytywanie par z localStorage
    useEffect(() => {
        const savedPairs = localStorage.getItem('pairsData');
        if (savedPairs) {
            try {
                setPairs(JSON.parse(savedPairs));
            } catch (error) {
                console.error('Błąd wczytywania par:', error);
            }
        }
    }, []);

    // Zapisywanie par do localStorage
    useEffect(() => {
        if (pairs.length > 0) {
            localStorage.setItem('pairsData', JSON.stringify(pairs));
        }
    }, [pairs]);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        setAnchorEl(null);
        navigate('/');
    };

    const handleClick = (index, name) => {
        return () => {
            switch(index) {
                case 0: // Panel hodowcy
                    navigate('/panel');
                    break;
                case 1: // Gołębie
                    navigate('/pigeons-new');
                    break;
                case 2: // Rodowody
                    navigate('/pedigree');
                    break;
                case 3: // Parowanie
                    // Już jesteśmy w parowaniu
                    break;
                case 4: // Loty
                    navigate('/flights');
                    break;
                default:
                    break;
            }
        };
    };

    const iconLabels = [
        'Panel hodowcy',
        'Gołębie',
        'Rodowody',
        'Parowanie',
        'Loty'
    ];

    const icons = [customIcon1, customIcon2, customIcon4, customIcon3, customIcon5];

    const handleAddPair = () => {
        setPairForm({
            maleId: '',
            femaleId: '',
            pairingDate: new Date().toISOString().split('T')[0],
            status: 'planned',
            comment: '',
            purpose: ''
        });
        setAddPairOpen(true);
    };

    const handleSavePair = () => {
        const male = pigeons.find(p => p.ID === pairForm.maleId);
        const female = pigeons.find(p => p.ID === pairForm.femaleId);

        if (!male || !female) {
            alert('Wybierz samca i samicę');
            return;
        }

        const newPair = {
            id: Date.now().toString(),
            maleId: pairForm.maleId,
            femaleId: pairForm.femaleId,
            male: male,
            female: female,
            pairingDate: pairForm.pairingDate,
            status: pairForm.status,
            comment: pairForm.comment,
            purpose: pairForm.purpose,
            eggs: [],
            offspring: [],
            createdAt: new Date().toISOString()
        };

        setPairs([...pairs, newPair]);
        setAddPairOpen(false);
    };

    const handleEditPair = (pair) => {
        setSelectedPair(pair);
        setPairForm({
            maleId: pair.maleId,
            femaleId: pair.femaleId,
            pairingDate: pair.pairingDate,
            status: pair.status,
            comment: pair.comment || '',
            purpose: pair.purpose || ''
        });
        setEditPairOpen(true);
    };

    const handleUpdatePair = () => {
        const updatedPairs = pairs.map(p => {
            if (p.id === selectedPair.id) {
                const male = pigeons.find(p => p.ID === pairForm.maleId);
                const female = pigeons.find(p => p.ID === pairForm.femaleId);
                return {
                    ...p,
                    maleId: pairForm.maleId,
                    femaleId: pairForm.femaleId,
                    male: male,
                    female: female,
                    pairingDate: pairForm.pairingDate,
                    status: pairForm.status,
                    comment: pairForm.comment,
                    purpose: pairForm.purpose
                };
            }
            return p;
        });
        setPairs(updatedPairs);
        setEditPairOpen(false);
        setSelectedPair(null);
    };

    const handleDeletePair = (pairId) => {
        if (window.confirm('Czy na pewno chcesz usunąć tę parę?')) {
            setPairs(pairs.filter(p => p.id !== pairId));
        }
    };

    const handleOpenPairDetails = (pair) => {
        setSelectedPair(pair);
        setPairDetailsOpen(true);
    };

    const getStatusLabel = (status) => {
        const labels = {
            'planned': 'Planowane',
            'in_nest': 'W gnieździe',
            'eggs': 'Jaja',
            'hatched': 'Wylęg',
            'weaned': 'Odstawione',
            'completed': 'Zakończone'
        };
        return labels[status] || status;
    };

    const getStatusColor = (status) => {
        const colors = {
            'planned': 'default',
            'in_nest': 'info',
            'eggs': 'warning',
            'hatched': 'primary',
            'weaned': 'success',
            'completed': 'default'
        };
        return colors[status] || 'default';
    };

    const filteredPairs = () => {
        let filtered = pairs;
        
        if (filter === 'active') {
            filtered = pairs.filter(p => p.status !== 'completed');
        } else if (filter === 'completed') {
            filtered = pairs.filter(p => p.status === 'completed');
        } else if (filter === 'season2026') {
            const year = new Date().getFullYear();
            filtered = pairs.filter(p => {
                const pairYear = new Date(p.pairingDate).getFullYear();
                return pairYear === year;
            });
        }
        
        return filtered;
    };

    // Filtrowanie gołębi według płci
    const getMales = () => pigeons.filter(p => p.gender === 'Samiec');
    const getFemales = () => pigeons.filter(p => p.gender === 'Samica');

    return (
        <div className={styles['content-container']}>
            <Grid container spacing={0} sx={{minHeight: '100vh', margin: 0}}>
                {/* Sidebar */}
                <Grid sx={{minHeight: '100vh', height: '100%', backgroundColor: '#478E94', position: { xs: 'relative', md: 'sticky' }, top: 0, bottom: 0, alignSelf: 'flex-start', margin: 0}} item xs={12} md={2}>
                    <Card sx={{
                        borderRadius: 0,
                        backgroundColor: '#478E94',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: { xs: 1, md: 2 },
                        boxShadow: 'none',
                    }}>
                        <CardMedia
                            component="img"
                            image={Logo}
                            alt="Logo"
                            sx={{
                                width: { xs: 120, md: '50%' },
                                maxWidth: '80%',
                                height: 'auto',
                                background: '#478E94',
                            }}
                        />
                    </Card>

                    <div className={styles['all-icons-container']}>
                        {icons.map((icon, index) => (
                            <div
                                className={styles['icon-container']}
                                key={index}
                                tabIndex="0"
                                onClick={handleClick(index, iconLabels[index])}
                                style={{
                                    backgroundColor: index === 3 ? '#4c4c4c' : '#478E94'
                                }}
                            >
                                <img className={styles['custom-icon']} src={icon} alt={`Custom Icon ${index + 1}`} />
                                <p className={styles['icon-label']}>{iconLabels[index]}</p>
                            </div>
                        ))}
                    </div>
                </Grid>

                {/* Main content */}
                <Grid item xs={12} md={10} sx={{height: { xs: 'auto', md: '100vh' }, overflowY: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column'}}>
                    <div className={styles['header-container']}>
                        <div className={styles['text-header-container']}>
                            <span className={styles['text-grey-header-catalog']}>Parowanie</span>
                        </div>

                        <div className={styles['avatar-container']}>
                            <Avatar className={styles.avatar} alt="Name" src={AvatarImage} />
                            <Menu
                                id="profile-menu"
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={handleMenuClose}>Profil</MenuItem>
                                <MenuItem onClick={handleMenuClose}>Ustawienia</MenuItem>
                                <MenuItem onClick={handleLogout}>Wyloguj</MenuItem>
                            </Menu>
                            <Typography sx={{fontSize: '18px', color: '#2A2A2A', fontFamily: "'Poppins', sans-serif", fontWeight: 500}}>Krzysztof</Typography>
                            <IconButton color="inherit" onClick={handleMenuOpen}>
                                <ExpandMoreIcon style={{fontSize: '3.6vh'}}/>
                            </IconButton>
                        </div>
                    </div>

                    {/* Main content area */}
                    <Box sx={{ padding: { xs: '16px', md: '20px 75px' }, marginBottom: 4 }}>
                        {/* Header with add button and filters */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, flexWrap: 'wrap', gap: 1.5 }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddPair}
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    textTransform: 'none',
                                    backgroundColor: '#57C09A',
                                    padding: '12px 30px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    '&:hover': {
                                        backgroundColor: '#4fcda2'
                                    }
                                }}
                            >
                                Nowa para
                            </Button>

                            {/* Filters */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                    label="Wszystkie"
                                    onClick={() => setFilter('all')}
                                    color={filter === 'all' ? 'primary' : 'default'}
                                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                                />
                                <Chip
                                    label="Aktywne"
                                    onClick={() => setFilter('active')}
                                    color={filter === 'active' ? 'primary' : 'default'}
                                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                                />
                                <Chip
                                    label="Zakończone"
                                    onClick={() => setFilter('completed')}
                                    color={filter === 'completed' ? 'primary' : 'default'}
                                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                                />
                                <Chip
                                    label="Sezon 2026"
                                    onClick={() => setFilter('season2026')}
                                    color={filter === 'season2026' ? 'primary' : 'default'}
                                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                                />
                            </Box>
                        </Box>

                        {/* Pairs list */}
                        <Grid container spacing={3}>
                            {filteredPairs().length === 0 ? (
                                <Grid item xs={12}>
                                    <Paper sx={{ padding: 4, textAlign: 'center' }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#707070', fontSize: '18px' }}>
                                            Brak par. Dodaj pierwszą parę, aby rozpocząć.
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ) : (
                                filteredPairs().map((pair) => {
                                    const male = pair.male || pigeons.find(p => p.ID === pair.maleId);
                                    const female = pair.female || pigeons.find(p => p.ID === pair.femaleId);
                                    
                                    return (
                                        <Grid item xs={12} md={6} lg={4} key={pair.id}>
                                            <Card
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        boxShadow: 4
                                                    }
                                                }}
                                                onClick={() => handleOpenPairDetails(pair)}
                                            >
                                                <Box sx={{ padding: 2 }}>
                                                    {/* Status and actions */}
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                                        <Chip
                                                            label={getStatusLabel(pair.status)}
                                                            color={getStatusColor(pair.status)}
                                                            size="small"
                                                            sx={{ fontFamily: "'Poppins', sans-serif" }}
                                                        />
                                                        <Box>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditPair(pair);
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeletePair(pair.id);
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>

                                                    {/* Pigeons images and info */}
                                                    <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                                                        {/* Male */}
                                                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                            <Avatar
                                                                src={male?.image || pigeonImage}
                                                                sx={{ width: 80, height: 80, margin: '0 auto 8px' }}
                                                            />
                                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', fontWeight: 600 }}>
                                                                Samiec
                                                            </Typography>
                                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#707070' }}>
                                                                {male?.number || 'Brak numeru'}
                                                            </Typography>
                                                        </Box>

                                                        <Typography sx={{ alignSelf: 'center', fontFamily: "'Poppins', sans-serif", fontSize: '20px', color: '#57C09A' }}>
                                                            ×
                                                        </Typography>

                                                        {/* Female */}
                                                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                            <Avatar
                                                                src={female?.image || pigeonImage}
                                                                sx={{ width: 80, height: 80, margin: '0 auto 8px' }}
                                                            />
                                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', fontWeight: 600 }}>
                                                                Samica
                                                            </Typography>
                                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#707070' }}>
                                                                {female?.number || 'Brak numeru'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Pairing date */}
                                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070', textAlign: 'center' }}>
                                                        Data parowania: {new Date(pair.pairingDate).toLocaleDateString('pl-PL')}
                                                    </Typography>

                                                    {/* Offspring count */}
                                                    {pair.offspring && pair.offspring.length > 0 && (
                                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#57C09A', textAlign: 'center', marginTop: 1 }}>
                                                            Młode: {pair.offspring.length}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Card>
                                        </Grid>
                                    );
                                })
                            )}
                        </Grid>
                    </Box>
                </Grid>
            </Grid>

            {/* Dialog dodawania pary */}
            <Dialog open={addPairOpen} onClose={() => setAddPairOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Dodaj nową parę
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                        {/* Wybór samca */}
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Samiec</InputLabel>
                            <Select
                                value={pairForm.maleId}
                                onChange={(e) => setPairForm({...pairForm, maleId: e.target.value})}
                                label="Samiec"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                {getMales().map((pigeon) => (
                                    <MenuItem key={pigeon.ID} value={pigeon.ID} sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                        {pigeon.number || pigeon.name || 'Brak numeru'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Wybór samicy */}
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Samica</InputLabel>
                            <Select
                                value={pairForm.femaleId}
                                onChange={(e) => setPairForm({...pairForm, femaleId: e.target.value})}
                                label="Samica"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                {getFemales().map((pigeon) => (
                                    <MenuItem key={pigeon.ID} value={pigeon.ID} sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                        {pigeon.number || pigeon.name || 'Brak numeru'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Data parowania */}
                        <TextField
                            fullWidth
                            label="Data parowania"
                            type="date"
                            value={pairForm.pairingDate instanceof Date 
                                ? pairForm.pairingDate.toISOString().split('T')[0]
                                : pairForm.pairingDate}
                            onChange={(e) => setPairForm({...pairForm, pairingDate: e.target.value})}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />

                        {/* Status */}
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Status</InputLabel>
                            <Select
                                value={pairForm.status}
                                onChange={(e) => setPairForm({...pairForm, status: e.target.value})}
                                label="Status"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                <MenuItem value="planned" sx={{ fontFamily: "'Poppins', sans-serif" }}>Planowane</MenuItem>
                                <MenuItem value="in_nest" sx={{ fontFamily: "'Poppins', sans-serif" }}>W gnieździe</MenuItem>
                                <MenuItem value="eggs" sx={{ fontFamily: "'Poppins', sans-serif" }}>Jaja</MenuItem>
                                <MenuItem value="hatched" sx={{ fontFamily: "'Poppins', sans-serif" }}>Wylęg</MenuItem>
                                <MenuItem value="weaned" sx={{ fontFamily: "'Poppins', sans-serif" }}>Odstawione</MenuItem>
                                <MenuItem value="completed" sx={{ fontFamily: "'Poppins', sans-serif" }}>Zakończone</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Cel parowania */}
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Cel parowania</InputLabel>
                            <Select
                                value={pairForm.purpose}
                                onChange={(e) => setPairForm({...pairForm, purpose: e.target.value})}
                                label="Cel parowania"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                <MenuItem value="racing" sx={{ fontFamily: "'Poppins', sans-serif" }}>Na loty</MenuItem>
                                <MenuItem value="sale" sx={{ fontFamily: "'Poppins', sans-serif" }}>Na sprzedaż</MenuItem>
                                <MenuItem value="line_test" sx={{ fontFamily: "'Poppins', sans-serif" }}>Test linii</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Komentarz */}
                        <TextField
                            fullWidth
                            label="Komentarz"
                            multiline
                            rows={3}
                            value={pairForm.comment}
                            onChange={(e) => setPairForm({...pairForm, comment: e.target.value})}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={() => setAddPairOpen(false)}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            color: '#707070'
                        }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        onClick={handleSavePair}
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': {
                                backgroundColor: '#4fcda2'
                            }
                        }}
                    >
                        Sparuj
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog edycji pary */}
            <Dialog open={editPairOpen} onClose={() => setEditPairOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Edytuj parę
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                        {/* Wybór samca */}
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Samiec</InputLabel>
                            <Select
                                value={pairForm.maleId}
                                onChange={(e) => setPairForm({...pairForm, maleId: e.target.value})}
                                label="Samiec"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                {getMales().map((pigeon) => (
                                    <MenuItem key={pigeon.ID} value={pigeon.ID} sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                        {pigeon.number || pigeon.name || 'Brak numeru'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Wybór samicy */}
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Samica</InputLabel>
                            <Select
                                value={pairForm.femaleId}
                                onChange={(e) => setPairForm({...pairForm, femaleId: e.target.value})}
                                label="Samica"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                {getFemales().map((pigeon) => (
                                    <MenuItem key={pigeon.ID} value={pigeon.ID} sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                        {pigeon.number || pigeon.name || 'Brak numeru'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Data parowania */}
                        <TextField
                            fullWidth
                            label="Data parowania"
                            type="date"
                            value={pairForm.pairingDate instanceof Date 
                                ? pairForm.pairingDate.toISOString().split('T')[0]
                                : pairForm.pairingDate}
                            onChange={(e) => setPairForm({...pairForm, pairingDate: e.target.value})}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />

                        {/* Status */}
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Status</InputLabel>
                            <Select
                                value={pairForm.status}
                                onChange={(e) => setPairForm({...pairForm, status: e.target.value})}
                                label="Status"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                <MenuItem value="planned" sx={{ fontFamily: "'Poppins', sans-serif" }}>Planowane</MenuItem>
                                <MenuItem value="in_nest" sx={{ fontFamily: "'Poppins', sans-serif" }}>W gnieździe</MenuItem>
                                <MenuItem value="eggs" sx={{ fontFamily: "'Poppins', sans-serif" }}>Jaja</MenuItem>
                                <MenuItem value="hatched" sx={{ fontFamily: "'Poppins', sans-serif" }}>Wylęg</MenuItem>
                                <MenuItem value="weaned" sx={{ fontFamily: "'Poppins', sans-serif" }}>Odstawione</MenuItem>
                                <MenuItem value="completed" sx={{ fontFamily: "'Poppins', sans-serif" }}>Zakończone</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Cel parowania */}
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Cel parowania</InputLabel>
                            <Select
                                value={pairForm.purpose}
                                onChange={(e) => setPairForm({...pairForm, purpose: e.target.value})}
                                label="Cel parowania"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                <MenuItem value="racing" sx={{ fontFamily: "'Poppins', sans-serif" }}>Na loty</MenuItem>
                                <MenuItem value="sale" sx={{ fontFamily: "'Poppins', sans-serif" }}>Na sprzedaż</MenuItem>
                                <MenuItem value="line_test" sx={{ fontFamily: "'Poppins', sans-serif" }}>Test linii</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Komentarz */}
                        <TextField
                            fullWidth
                            label="Komentarz"
                            multiline
                            rows={3}
                            value={pairForm.comment}
                            onChange={(e) => setPairForm({...pairForm, comment: e.target.value})}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={() => setEditPairOpen(false)}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            color: '#707070'
                        }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        onClick={handleUpdatePair}
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': {
                                backgroundColor: '#4fcda2'
                            }
                        }}
                    >
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog szczegółów pary */}
            <Dialog
                open={pairDetailsOpen}
                onClose={() => setPairDetailsOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Szczegóły pary
                </DialogTitle>
                <DialogContent sx={{ padding: 0 }}>
                    {selectedPair && (
                        <Box sx={{ padding: 3 }}>
                            {/* Pigeons info */}
                            <Box sx={{ display: 'flex', gap: 3, marginBottom: 3 }}>
                                <Box sx={{ flex: 1, textAlign: 'center' }}>
                                    <Avatar
                                        src={selectedPair.male?.image || pigeonImage}
                                        sx={{ width: 120, height: 120, margin: '0 auto 12px' }}
                                    />
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: 600 }}>
                                        Samiec
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                        {selectedPair.male?.number || 'Brak numeru'}
                                    </Typography>
                                </Box>

                                <Box sx={{ flex: 1, textAlign: 'center' }}>
                                    <Avatar
                                        src={selectedPair.female?.image || pigeonImage}
                                        sx={{ width: 120, height: 120, margin: '0 auto 12px' }}
                                    />
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: 600 }}>
                                        Samica
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                        {selectedPair.female?.number || 'Brak numeru'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Timeline */}
                            <Box sx={{ marginBottom: 3 }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '18px', fontWeight: 600, marginBottom: 2 }}>
                                    Timeline lęgowy
                                </Typography>
                                <Paper sx={{ padding: 2 }}>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                        Parowanie: {new Date(selectedPair.pairingDate).toLocaleDateString('pl-PL')}
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                        Status: {getStatusLabel(selectedPair.status)}
                                    </Typography>
                                </Paper>
                            </Box>

                            {/* Offspring section */}
                            <Box>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '18px', fontWeight: 600, marginBottom: 2 }}>
                                    Potomstwo
                                </Typography>
                                {selectedPair.offspring && selectedPair.offspring.length > 0 ? (
                                    <Grid container spacing={2}>
                                        {selectedPair.offspring.map((offspring, index) => (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Card>
                                                    <Box sx={{ padding: 2 }}>
                                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 600 }}>
                                                            {offspring.ringNumber || `Młode ${index + 1}`}
                                                        </Typography>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                        Brak potomstwa
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={() => setPairDetailsOpen(false)}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            color: '#707070'
                        }}
                    >
                        Zamknij
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Pairing;
